/**
 * Time and date formatting utilities.
 *
 * All functions accept nullable inputs and return a safe display string.
 * Pure functions — no side effects, no DOM access.
 *
 * `formatAge` and `formatRelativeTime` are the cross-client OWNERS of the
 * relative-age display rules. iOS mirrors them in
 * `ios/Kingfisher/RelativeTime.swift` and Android in
 * `android/.../ui/components/RelativeTime.kt`;
 * `backend/tests/test_relative_age_client_parity.py` pins the mirrors
 * (thresholds, rounding, suffixes) to this module. Change the rules here
 * first, then update both mirrors, or the parity test fails CI.
 */

/**
 * Compact relative age: "3m ago", "5h ago", "2d ago".
 * Rounded; hours roll over to days at 48h.
 * Omits "ago" suffix when `compact` is true (e.g. for table columns).
 */
export function formatAge(
  dateStr: string | undefined | null,
  options?: { compact?: boolean; now?: number },
): string {
  if (!dateStr) return '';
  const parsed = Date.parse(dateStr);
  if (Number.isNaN(parsed)) return '';
  const nowMs = options?.now ?? Date.now();
  // Clamp to 0 so future-dated rows never render as "-5h ago".
  const hours = Math.max(0, nowMs - parsed) / 3_600_000;
  const suffix = options?.compact ? '' : ' ago';
  if (hours < 1) return `${Math.round(hours * 60)}m${suffix}`;
  if (hours < 48) return `${Math.round(hours)}h${suffix}`;
  return `${Math.round(hours / 24)}d${suffix}`;
}

/**
 * Relative time with "just now" for very recent and precision for larger spans.
 * Rounded; hours roll over to days at 24h.
 */
export function formatRelativeTime(
  iso: string | undefined | null,
  options?: { now?: number },
): string {
  if (!iso) return '';
  const parsed = new Date(iso).getTime();
  if (Number.isNaN(parsed)) return '';
  const nowMs = options?.now ?? Date.now();
  const mins = Math.round(Math.max(0, nowMs - parsed) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

/**
 * Absolute date+time: "May 22 14:30" (omits year if same year).
 * Falls back to em dash for null/undefined.
 */
export function formatDateTime(
  val: string | undefined | null,
  options?: { fallback?: string; now?: Date },
): string {
  if (!val) return options?.fallback ?? '—';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return options?.fallback ?? '—';
  const now = options?.now ?? new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  const datePart = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(!sameYear && { year: '2-digit' }),
  });
  const timePart = d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${datePart} ${timePart}`;
}

/**
 * Full locale date+time string, e.g. "5/22/2026, 2:30:00 PM".
 */
export function formatDate(
  value: string | number | null | undefined,
  options?: { fallback?: string; localeOptions?: Intl.DateTimeFormatOptions },
): string {
  if (value == null || value === '') return options?.fallback ?? '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return options?.fallback ?? '';
  return date.toLocaleString(undefined, options?.localeOptions);
}

/** Compact elapsed duration: "45s" or "2m 5s". */
export function formatDuration(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}
